import { Mutation } from '@apollo/react-components';
import PropTypes from 'prop-types';
import React, { ComponentProps, ReactElement, ReactNode, useContext, useRef, useState } from 'react';
import { Button } from './Button';
import { serviceActions } from '../../state/modules/service';
import { checkAction } from '../../utils/OperationPermissions';
import handleTemporarySave from './utils/handleTemporarySave';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import getIn from './utils/getIn';
import AppContext from '../Context/AppContext';
import { useDispatch } from 'react-redux';
import ActionButtonContext from '../Context/ActionButtonContext';
import { ActionButton } from './Button/Button';
import { ActionMenuTheme, InfoTheme } from '../../styles/themes/default/defaultFluentTheme';
import { ContextualMenu, ContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import WorkspaceContext from '../Context/WorkspaceContext';
import getClientMutationId from '../../utils/getClientMutationId';
import { useRefFunction } from '../../utils/hooks';
import { IActionType, usePerformSave } from './Hooks/usePerformSave';
import { ContextualMenuItemType, IComponentAs, IContextualMenuProps } from '@fluentui/react';
import { useHistory } from 'react-router';
import { EntityType, IDeprecatedFormAction, IFormAction } from '../../@types/entityTypes';
import { isDeprecatedWorkspaceActionType, isWorkspaceActionType } from '../Workspace/WorkspaceActionButtons';
import { AlertType } from '../../graphql/schema/p4types';
import type { IContextualMenuItemProps } from '@fluentui/react/src/components/ContextualMenu/ContextualMenuItem.types';
import { Dispatch } from 'redux';
import FluentModal from '../modals/FluentModal';

//This is a bit of a pointless wrapper, but saves on bigger changes.
//Don't want to declare it inline because it will force unnecessary remounts.
export const ItemRenderer: IComponentAs<IContextualMenuItemProps> = (props) => {
  if (props.item.key && props.item.text) {
    return <ContextualMenuItem {...props} data-testid={props.item.key} style={{ marginLeft: '10px' }} />;
  }
  return props.item.element || null;
};

//Action buttons on forms have custom item rendering, and come out a pixel or 2 too tall causing a pointless scrollbar.
//This is just to hide it...
export const HiddenVerticalScrollMenu: IComponentAs<IContextualMenuProps> = (props) => {
  return (
    <ContextualMenu
      {...props}
      styles={{
        list: {
          overflowY: 'hidden',
          backgroundColor: '#fff',
        },
        subComponentStyles: {
          menuItem: {
            label: {
              marginLeft: '15px',
            },
          },
        },
        header: {
          padding: '0px',
        },
      }}
      theme={ActionMenuTheme}
    />
  );
};

const useActionPermissionCheck = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const app = useContext(AppContext);
  const { defaultContextValue: isNotAWorkspace } = useContext(WorkspaceContext);

  const { screenReference, entity } = connectedForm;

  return (_actionButton: IFormAction<any> | IDeprecatedFormAction<any>) => {
    return (
      checkAction({ screenReference, entity, app }, _actionButton) &&
      (!_actionButton.condition ||
        //'any' override is due to the second argument - Do not believe it is in use, but hesitant to remove in case of side effects.
        (_actionButton.condition as any)(connectedForm.values, isNotAWorkspace ? 'FORM' : 'WORKSPACE'))
    );
  };
};

function isFormActionType(
  actionButton: IFormAction<any> | IDeprecatedFormAction<any>
): actionButton is IFormAction<any> {
  return (actionButton as IFormAction<any>).text !== undefined;
}

/**
 * @deprecated - New actions should not be written to use this - Use IFormAction and self-handle the Modal.
 */
const TriggerModalActionButton = ({
  id,
  individualButtons,
  component,
  label,
  modalContent,
  modalProps,
  actionButton,
}: {
  id: string;
  individualButtons: boolean;
  component: any;
  label: string;
  modalContent: any;
  modalProps: any;
  actionButton: IDeprecatedFormAction<any> & IActionType;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const connectedForm = useContext(ConnectedFormContext);

  const onSaveComplete = useRefFunction(() => {
    setIsOpen(true);
  });

  //Re-implements performSave/performTemporarySave from ActionButtons because we need differing 'onComplete' behaviour.
  const [performSave, confirmationPrompt] = usePerformSave(onSaveComplete);

  const onClick = () => {
    if (actionButton.performSave && connectedForm.dirty) {
      return performSave(actionButton as IActionType);
    }
    setIsOpen(true);
  };

  const Content = modalContent;
  return (
    <div style={{ order: 4, justifyContent: 'flex-end' }}>
      {confirmationPrompt}
      <FluentModal isOpen={isOpen}>
        <div style={{ height: '90vh', width: '80vw', position: 'relative' }}>
          <Content {...modalProps} onClose={() => setIsOpen(false)} />
        </div>
      </FluentModal>
      <ActionButton
        data-testid={id}
        id={id}
        tabIndex={1}
        style={{
          order: 4,
          display: 'flex',
          flexGrow: 1,
          justifyContent: individualButtons ? 'center' : 'left',
        }}
        individualButtonComponent={component}
        label={label}
        onClick={onClick}
      />
    </div>
  );
};

//These props are provided only so that old format actions using 'fn' and expecting them to be in 'props' continue to function.
//Once all actions are migrated to 'IFormAction' from 'IDeprecatedFormAction', these can be removed (As can the wrapper that provides them).
type IDeprecatedActionButtonsProps = { connectedForm: any; dispatch: Dispatch; app: any; customAlertHandler?: any };

const ActionButtons = (
  props: {
    actionButtons: Array<IFormAction<any> | IDeprecatedFormAction<any>>;
    entity: EntityType;
    values: any;
  } & IDeprecatedActionButtonsProps
) => {
  const actionWarningSentRef = useRef<{ [key: number]: boolean }>({});

  const history = useHistory();
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const { actionButtons, entity, values } = props;

  const handleActionOption = (action: IDeprecatedFormAction<any> & IActionType) => {
    if (action && action.performSave && connectedForm.dirty) {
      return performSave(action);
    }
    if (action && action.fn) {
      return action.fn(props);
    }
  };

  const handleMutationOption = (action: IDeprecatedFormAction<any>, callMutation: any) => {
    if (action && action.performTemporarySave) {
      return performTemporarySave(action, (result?: any, _action?: any) => {
        if (result && result.data && _action.processData) {
          //If we want to use the updated value for the mutation, need to process it (As don't know the structure of the temp save here).
          callMutation(_action.fn(_action.processData(result.data)));
        } else {
          //Product Price Matrix usage does not need it, we can pass the pre-temporary value.
          callMutation(_action.fn(props));
        }
      });
    }
    callMutation(action.fn?.(props));
  };

  const onSaveComplete = useRefFunction((result: any, action: any) => {
    //Maintains support for older implementation - Prefer 'onSaveComplete' on the action (Which is called from usePerformSave itself).
    if (action.fn) {
      action.fn(props, { result });
    }
  });

  const [performSave, confirmationPrompt] = usePerformSave(onSaveComplete);

  const checkActionPermissions = useActionPermissionCheck();

  const performTemporarySave: (result?: any, _action?: any) => Promise<any> = (
    action,
    onTemporaryCompleted = () => {}
  ) => {
    //Passing an empty function as default to avoid errors when no 'on complete' is provided.
    const mutationId = getClientMutationId();
    const submitPromise = handleTemporarySave({ connectedForm }, mutationId);
    return submitPromise.then(
      (result: any) => onTemporaryCompleted(result, action),
      connectedForm.submitFunctions?.submitFailed
    );
  };

  const permittedActionButtons = actionButtons.filter((_actionButton) => checkActionPermissions(_actionButton));
  if (permittedActionButtons.length === 0) {
    return null;
  }
  //If there is only a single button to go into the actions menu, display it as an individual button instead.
  const correctedActionButtons =
    permittedActionButtons.filter((_actionButton) => !_actionButton.individualButton).length === 1
      ? permittedActionButtons.map((actionButton) => ({ ...actionButton, individualButton: true }))
      : permittedActionButtons;

  const formatLabel = (_actionButton: any) => {
    let label =
      _actionButton.description instanceof Function
        ? _actionButton.description(connectedForm.values)
        : _actionButton.description || '';
    if (typeof label === 'string') {
      label = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
    }
    return label;
  };

  const renderActions = (individualButtons: boolean) => {
    if (correctedActionButtons.length === 0) {
      return null;
    }

    const renderedButtons: Array<IFormAction<any> | ReactNode | ReactElement | { element: ReactNode | ReactElement }> =
      correctedActionButtons
        .filter((_actionButton) => !!_actionButton.individualButton === individualButtons)
        // eslint-disable-next-line complexity
        .map((_actionButton, index) => {
          const key = `actionButton${index}`;

          if (isFormActionType(_actionButton)) {
            //Ideal path - Provided a valid actionButton object.
            if (_actionButton.individualButton) {
              const individualButtonComponent =
                _actionButton.individualButtonComponent ?? (_actionButton.buttonProps ? Button : undefined);
              return {
                key: key,
                element: (
                  <ActionButton
                    key={key}
                    disabled={_actionButton.disabled}
                    onClick={_actionButton.onClick as any}
                    label={_actionButton.text}
                    buttonProps={_actionButton.buttonProps}
                    individualButtonComponent={individualButtonComponent}
                  />
                ),
              };
            }
            if (_actionButton.buttonProps?.iconProps) {
              return { ..._actionButton, iconProps: _actionButton.buttonProps.iconProps };
            }
            return _actionButton;
          }
          const label = formatLabel(_actionButton);
          const id = `formAction_${label.replace(' ', '_')}`;

          const { component, triggerModal } = _actionButton;

          if (_actionButton.element) {
            //New 'element' option - Just allow providing the actual button.
            //Hooks/etc mean we don't really need the callbacks/etc from here.
            return { key: key, ..._actionButton };
          } else if (
            triggerModal &&
            (getIn(values, triggerModal.entityId) || _actionButton.performSave || _actionButton.alwaysShow)
          ) {
            const entityId = getIn(values, triggerModal.entityId); //Needs to be getIn as format of 'triggerModal.entityId' may be complex.

            const triggerModalDefaultProps = {
              entityId: entityId,
              match: { params: { id: entityId } },
            };
            const modalProps = triggerModal.modalProps
              ? { ...triggerModal.modalProps, ...triggerModalDefaultProps, formData: props }
              : triggerModalDefaultProps;
            if (entity && !modalProps.overrideEntity) modalProps.entity = entity;
            if (values) modalProps.values = values;
            _actionButton.index = index;
            if (connectedForm.readOnly) _actionButton.performSave = false;
            return {
              key: key,
              element: (
                <TriggerModalActionButton
                  key={key}
                  modalContent={triggerModal.modalContent}
                  id={id}
                  modalProps={modalProps}
                  individualButtons={individualButtons}
                  label={label}
                  component={component}
                  actionButton={_actionButton}
                />
              ),
            };
          } else if (component) {
            const justifyContent = individualButtons ? 'center' : 'left';

            if (_actionButton.mutationWrapper && _actionButton.mutation) {
              const onCompleted = (data: any) => {
                if (_actionButton.mutationWrapper?.onCompleteMutation) {
                  _actionButton.mutationWrapper.onCompleteMutation(data, props);
                  return;
                }
                dispatch(
                  serviceActions.genericAlert(
                    _actionButton.mutationWrapper?.subjectSuccess,
                    _actionButton.mutationWrapper?.messageSuccess,
                    AlertType.Info
                  )
                );
                history.goBack();
              };

              const onError = (data: any) => {
                if (_actionButton.mutationWrapper?.onError) {
                  _actionButton.mutationWrapper.onError(data);
                }
              };

              return {
                key: key,
                element: (
                  <Mutation key={key} mutation={_actionButton.mutation} onCompleted={onCompleted} onError={onError}>
                    {(callMutation: any) => {
                      return (
                        <ActionButton
                          onClick={(e) => {
                            e.preventDefault();
                            handleMutationOption(_actionButton, callMutation);
                          }}
                          data-testid={id}
                          style={{ order: 4, display: 'flex', flexGrow: 1, justifyContent: justifyContent }}
                          individualButtonComponent={component}
                          label={label}
                        />
                      );
                    }}
                  </Mutation>
                ),
              };
            }
            return {
              key: key,
              element: (
                <ActionButton
                  key={key}
                  onClick={(e) => {
                    e?.preventDefault?.();
                    handleActionOption(_actionButton);
                  }}
                  style={{ order: 4, display: 'flex', flexGrow: 1, justifyContent: justifyContent }}
                  individualButtonComponent={component}
                  label={label}
                  data-testid={_actionButton['data-testid']}
                />
              ),
            };
          } else if (
            !actionWarningSentRef.current[index] &&
            !isWorkspaceActionType(_actionButton) &&
            !isDeprecatedWorkspaceActionType(_actionButton)
          ) {
            //CJS - If action button doesn't match above configurations, and isn't a workspace action, throw a warning as may be configured incorrectly.
            //TODO - New WorkspaceActions should never get here because they should be defined in hooks and not be passed as Form Actions.
            //     - Old format actions to be replaced over time.
            console.warn('Action Button not defined for Form', _actionButton);
            actionWarningSentRef.current[index] = true; //Avoid spamming the console too much.
          }

          return null;
        })
        .filter((item) => !!item); //Filter out nulls (E.g. Workspace actions that won't be displayed on the form).

    if (individualButtons) {
      return renderedButtons.filter((item: any) => !!item.element).map((item: any) => item.element);
    } else if (renderedButtons.length === 0) {
      return null;
    }

    return (
      <Button
        text="Actions"
        menuAs={HiddenVerticalScrollMenu}
        menuProps={{
          contextualMenuItemAs: ItemRenderer,
          items: [
            {
              key: 'actionsHeader',
              itemType: ContextualMenuItemType.Header,
              text: 'General',
            },
            ...(renderedButtons as any),
          ],
        }}
        data-testid="formContextButton_"
        persistMenu
        theme={InfoTheme}
        tabIndex={1}
        style={{ order: 3 }}
      />
    );
  };

  const actionButton = renderActions(false);
  const individualActionButtons = renderActions(true);
  if (actionButton || (individualActionButtons && (individualActionButtons as Array<any>)?.length > 0)) {
    return (
      <>
        {confirmationPrompt}
        <ActionButtonContext.Provider
          value={{
            individualButtons: false,
            performSave: performSave,
          }}
        >
          {actionButton}
        </ActionButtonContext.Provider>
        <ActionButtonContext.Provider
          value={{
            individualButtons: true,
            performSave: performSave,
          }}
        >
          {individualActionButtons}
        </ActionButtonContext.Provider>
      </>
    );
  }
  return null;
};

ActionButtons.propTypes = {
  values: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  actionButtons: PropTypes.array,
};

ActionButtons.defaultProps = {
  values: {},
  actionButtons: [],
};

export default (
  props: Omit<ComponentProps<typeof ActionButtons>, 'connectedForm' | 'app' | 'dispatch' | 'values' | 'entity'> & {
    entity?: EntityType;
  }
) => {
  const connectedForm = useContext(ConnectedFormContext);
  const app = useContext(AppContext);
  const dispatch = useDispatch();

  return (
    <ActionButtons
      {...props}
      connectedForm={connectedForm}
      app={app}
      dispatch={dispatch}
      values={connectedForm.values}
      entity={props.entity ?? connectedForm.entity}
    />
  );
};
